import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import AIDisclaimer from '@/components/legal/AIDisclaimer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

function IKPackageViewer() {
  const navigate = useNavigate();
  const { packageId } = useParams();
  const { user, organization, logout } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [package_, setPackage] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [cloudProviders, setCloudProviders] = useState([]);
  const [showCloudBackupMenu, setShowCloudBackupMenu] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  useEffect(() => {
    fetchPackage();
    fetchCloudProviders();
  }, [packageId]);

  const fetchPackage = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(
        `${API}/ik-packages/${packageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPackage(response.data);
    } catch (error) {
      console.error('Failed to fetch package:', error);
      toast.error('Kunne ikke laste IK-pakke');
    } finally {
      setLoading(false);
    }
  };

  const fetchCloudProviders = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(
        `${API}/cloud-storage/connected`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCloudProviders(response.data.connected || []);
    } catch (error) {
      // Silently fail - cloud storage might not be configured
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleExportWord = async () => {
    const toastId = toast.loading('Genererer Word-dokument...');
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(
        `${API}/ik-packages/${packageId}/export/word`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `IK_System_${organization?.name?.replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('✅ Word-dokument lastet ned!', { id: toastId });
    } catch (error) {
      console.error('Failed to export Word:', error);
      toast.error('❌ Kunne ikke eksportere til Word', { id: toastId });
    }
  };

  const handleExportExcel = async () => {
    const toastId = toast.loading('Genererer Excel-fil...');
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(
        `${API}/ik-packages/${packageId}/export/excel`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `IK_System_${organization?.name?.replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('✅ Excel-fil lastet ned!', { id: toastId });
    } catch (error) {
      console.error('Failed to export Excel:', error);
      toast.error('❌ Kunne ikke eksportere til Excel', { id: toastId });
    }
  };

  const handleCloudBackup = async (provider) => {
    setShowCloudBackupMenu(false);
    setBackingUp(true);
    
    const providerName = provider === 'google_drive' ? 'Google Drive' : 'OneDrive';
    const toastId = toast.loading(`Sikkerhetskopierer til ${providerName}...`);
    
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(
        `${API}/cloud-storage/${provider}/backup-ik-package/${packageId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`✅ Sikkerhetskopiert til ${providerName}!`, { id: toastId });
      
      if (response.data.link) {
        toast.success(
          <span>
            Se filen: <a href={response.data.link} target="_blank" rel="noopener noreferrer" className="underline">Åpne i {providerName}</a>
          </span>,
          { duration: 5000 }
        );
      }
    } catch (error) {
      console.error('Cloud backup error:', error);
      toast.error(error.response?.data?.detail || `❌ Kunne ikke sikkerhetskopiere til ${providerName}`, { id: toastId });
    } finally {
      setBackingUp(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Laster IK-pakke...</p>
        </div>
      </div>
    );
  }

  if (!package_) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">IK-pakke ikke funnet</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Tilbake til dashboard
          </button>
        </div>
      </div>
    );
  }

  const lov_matrise = package_.lov_og_forskriftsmatrise || {};
  const prosedyrer = package_.prosedyrer || {};
  const aarshjul = package_.aarshjul || [];
  const ik_dokumentasjon = package_.ik_package?.ik_dokumentasjon || {};
  const sertifiseringer = package_.sertifiseringer || null;
  const spesialmoduler = package_.spesialmoduler || null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/k46v5h98_Systemklar%20Logo%20med%20Sjekkmerke.png" 
                alt="Systemklar Logo" 
                className="h-10"
              />
              <span className="text-gray-500">Generert IK-System</span>
            </div>
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/eui6rlb9_Hovedlogo%20Valoria%20%28uten%20bakgrunn%29.png" 
                alt="Valoria Solutions" 
                className="h-6"
              />
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500">{organization?.name}</p>
              </div>
              <button onClick={logout} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                Logg ut
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <Link to="/dashboard" className="text-blue-600 hover:text-blue-500">
            ← Tilbake til dashboard
          </Link>
        </nav>

        {/* Success Banner */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h2 className="text-2xl font-bold text-gray-900">
                Komplett IK §5-system generert!
              </h2>
              <p className="text-gray-600 mt-1">
                Generert: {new Date(package_.created_at).toLocaleString('no-NO')}
              </p>
            </div>
          </div>
        </div>

        {/* NACE-modul badge (vises kun når bransjespesifikk modul er aktivert) */}
        {package_.nace_module?.badge_label && (
          <div
            className="mb-6 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4"
            data-testid="nace-module-badge"
          >
            <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white text-sm font-bold">
              ✓
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-emerald-900">
                {package_.nace_module.badge_label} aktivert
              </p>
              <p className="mt-1 text-xs text-emerald-800">
                Basert på NACE{' '}
                <span className="font-mono font-semibold">{package_.nace_module.nace_code}</span>{' '}
                er IK-systemet ditt utvidet med bransjespesifikke lover, årshjul-oppgaver og AI-veiledning.
              </p>
            </div>
          </div>
        )}

        {/* AI Disclaimer - shown above generated content */}
        <div className="mb-8">
          <AIDisclaimer variant="after" />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mb-8 flex-wrap">
          <button
            onClick={handleExportWord}
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Eksporter til Word
          </button>
          <button
            onClick={handleExportExcel}
            className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Eksporter til Excel
          </button>
          
          {/* Cloud Backup Button */}
          <div className="relative">
            <button
              onClick={() => setShowCloudBackupMenu(!showCloudBackupMenu)}
              disabled={cloudProviders.length === 0}
              className={`px-6 py-3 rounded-md font-medium flex items-center ${
                cloudProviders.length > 0
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              title={cloudProviders.length === 0 ? 'Koble til sky-lagring først' : 'Sikkerhetskopier til sky'}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {backingUp ? 'Sikkerhetskopierer...' : 'Sikkerhetskopi til sky'}
            </button>
            
            {showCloudBackupMenu && cloudProviders.length > 0 && (
              <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-10">
                <div className="py-1">
                  {cloudProviders.map(provider => (
                    <button
                      key={provider.provider}
                      onClick={() => handleCloudBackup(provider.provider)}
                      disabled={backingUp}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                    >
                      {provider.provider === 'google_drive' ? '📁 Google Drive' : '☁️ OneDrive'}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {cloudProviders.length === 0 && (
            <Link
              to="/documents"
              className="px-6 py-3 border border-purple-300 text-purple-600 rounded-md hover:bg-purple-50 font-medium flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              Koble til sky-lagring
            </Link>
          )}
        </div>

        {/* Content Sections */}
        <div className="space-y-6">
          {/* § 5.1 - Lov og forskriftsmatrise */}
          <Section
            title="§ 5.1 - Lov- og forskriftsmatrise"
            icon="📋"
            count={`${lov_matrise.lover?.length || 0} lover`}
            expanded={expandedSections['lov']}
            onToggle={() => toggleSection('lov')}
          >
            <div className="space-y-4">
              {lov_matrise.lover?.map((lov, idx) => (
                <div key={idx} className="border-l-4 border-blue-500 pl-4 py-2">
                  <h4 className="font-semibold text-gray-900">{lov.lov}</h4>
                  <div className="mt-2 space-y-2 text-sm text-gray-700">
                    <p><strong>Praksis:</strong> {lov.praksis}</p>
                    <p><strong>Dokumentasjon:</strong> {lov.dokumentasjon}</p>
                    <p><strong>Tiltak:</strong> {lov.tiltak}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* § 5.2-5.4 - Prosedyrer */}
          <Section
            title="§ 5.4 - HMS-policy og mål"
            icon="🎯"
            count="Policy generert"
            expanded={expandedSections['policy']}
            onToggle={() => toggleSection('policy')}
          >
            <div className="prose max-w-none">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                {prosedyrer.hms_policy || 'Ikke generert'}
              </pre>
            </div>
          </Section>

          <Section
            title="§ 5.5 - Organisering og ansvar"
            icon="👥"
            count="Organisasjonskart"
            expanded={expandedSections['org']}
            onToggle={() => toggleSection('org')}
          >
            <div className="prose max-w-none">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                {prosedyrer.organisering_og_ansvar || 'Ikke generert'}
              </pre>
            </div>
          </Section>

          <Section
            title="§ 5.6 - Risikovurdering"
            icon="⚠️"
            count="Mal med eksempler"
            expanded={expandedSections['risk']}
            onToggle={() => toggleSection('risk')}
          >
            <div className="prose max-w-none">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                {prosedyrer.risikovurdering_mal || 'Ikke generert'}
              </pre>
            </div>
          </Section>

          <Section
            title="§ 5.7 - Avvikshåndtering"
            icon="🔧"
            count="Rutine generert"
            expanded={expandedSections['avvik']}
            onToggle={() => toggleSection('avvik')}
          >
            <div className="prose max-w-none">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                {prosedyrer.avvikssystem || 'Ikke generert'}
              </pre>
            </div>
          </Section>

          <Section
            title="§ 5.2 - Opplæringsplan"
            icon="🎓"
            count="Plan generert"
            expanded={expandedSections['training']}
            onToggle={() => toggleSection('training')}
          >
            <div className="prose max-w-none">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                {prosedyrer.opplaeringsplan || 'Ikke generert'}
              </pre>
            </div>
          </Section>

          <Section
            title="🚨 Beredskapsplan"
            icon="🚨"
            count="Plan generert"
            expanded={expandedSections['emergency']}
            onToggle={() => toggleSection('emergency')}
          >
            <div className="prose max-w-none">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                {prosedyrer.beredskapsplan || 'Ikke generert'}
              </pre>
            </div>
          </Section>

          {/* § 5.8 - Årshjul */}
          <Section
            title="§ 5.8 - Årshjul (Systematisk overvåkning)"
            icon="📅"
            count={`${aarshjul.length} oppgaver`}
            expanded={expandedSections['wheel']}
            onToggle={() => toggleSection('wheel')}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {aarshjul.map((oppgave, idx) => (
                <div key={idx} className="border border-gray-200 rounded-md p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-2">
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                      {['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'][oppgave.måned - 1]}
                    </span>
                    {oppgave.kategori && (
                      <span className="ml-2 text-xs text-gray-500">{oppgave.kategori}</span>
                    )}
                  </div>
                  <h4 className="font-semibold text-gray-900 text-sm">{oppgave.oppgave}</h4>
                  <p className="text-xs text-gray-600 mt-1">Ansvarlig: {oppgave.ansvarlig}</p>
                </div>
              ))}
            </div>
          </Section>

          {/* Sertifiseringsmoduler */}
          {sertifiseringer && sertifiseringer.valgte_sertifiseringer && sertifiseringer.valgte_sertifiseringer.length > 0 && (
            <>
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg p-6 mb-6">
                <div className="flex items-center">
                  <span className="text-3xl mr-3">🏆</span>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Sertifiseringsmoduler
                    </h3>
                    <p className="text-gray-600">
                      Gap-analyse og tilleggskrav for: {sertifiseringer.valgte_sertifiseringer.join(', ')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Felles krav */}
              <Section
                title="Felles krav på tvers av standarder"
                icon="🔗"
                count={`${sertifiseringer.felles_krav?.length || 0} krav`}
                expanded={expandedSections['felles']}
                onToggle={() => toggleSection('felles')}
              >
                <div className="space-y-4">
                  {sertifiseringer.felles_krav?.map((krav, idx) => (
                    <div key={idx} className="border-l-4 border-purple-500 pl-4 py-3 bg-white rounded-r-md">
                      <h4 className="font-semibold text-gray-900">{krav.krav}</h4>
                      <div className="mt-2 space-y-2 text-sm">
                        <p><strong>Gjelder:</strong> {krav.standarder?.join(', ')}</p>
                        <p><strong>Status:</strong> <span className="text-blue-600">{krav.status}</span></p>
                        {krav.gap && (
                          <p className="text-orange-600"><strong>Gap:</strong> {krav.gap}</p>
                        )}
                        {krav.anbefaling && (
                          <p className="text-green-600"><strong>Anbefaling:</strong> {krav.anbefaling}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Spesifikke krav per sertifisering */}
              {Object.entries(sertifiseringer.spesifikke_krav || {}).map(([certId, data]) => {
                const certName = {
                  'miljofyrtarn': 'Miljøfyrtårn',
                  'iso_45001': 'ISO 45001',
                  'iso_14001': 'ISO 14001',
                  'iso_9001': 'ISO 9001'
                }[certId] || certId;

                return (
                  <Section
                    key={certId}
                    title={`${certName} - Spesifikke krav`}
                    icon="📋"
                    count="Tilleggskrav"
                    expanded={expandedSections[certId]}
                    onToggle={() => toggleSection(certId)}
                  >
                    <div className="space-y-4">
                      {data.tilleggsprosedyrer && data.tilleggsprosedyrer.length > 0 && (
                        <div>
                          <h5 className="font-semibold text-gray-900 mb-2">Tilleggsprosedyrer:</h5>
                          <ul className="list-disc pl-5 space-y-1">
                            {data.tilleggsprosedyrer.map((proc, idx) => (
                              <li key={idx} className="text-gray-700">{proc}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {data.dokumentasjon && data.dokumentasjon.length > 0 && (
                        <div>
                          <h5 className="font-semibold text-gray-900 mb-2">Dokumentasjonskrav:</h5>
                          <ul className="list-disc pl-5 space-y-1">
                            {data.dokumentasjon.map((doc, idx) => (
                              <li key={idx} className="text-gray-700">{doc}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {data.implementeringstips && (
                        <div className="bg-blue-50 p-4 rounded-md">
                          <p className="text-sm text-blue-800">
                            <strong>💡 Implementeringstips:</strong> {data.implementeringstips}
                          </p>
                        </div>
                      )}
                    </div>
                  </Section>
                );
              })}
            </>
          )}

          {/* Bransjespesifikke spesialmoduler */}
          {spesialmoduler && (
            <>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-lg p-6 mb-6">
                <div className="flex items-center">
                  <span className="text-3xl mr-3">⚙️</span>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Bransjespesifikke spesialmoduler
                    </h3>
                    <p className="text-gray-600">
                      {spesialmoduler.sha_plan && 'SHA-plan for Byggherreforskriften'}
                      {spesialmoduler.haccp && 'HACCP-system for mattrygghet'}
                      {spesialmoduler.legionella && 'Legionellakontrollplan for varmtvannssystemer'}
                    </p>
                  </div>
                </div>
              </div>

              {/* SHA-plan (Bygg/Anlegg) */}
              {spesialmoduler.sha_plan && (
                <>
                  <Section
                    title="SHA-plan - Organisering"
                    icon="🏗️"
                    count="Byggherreforskriften"
                    expanded={expandedSections['sha_org']}
                    onToggle={() => toggleSection('sha_org')}
                  >
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                        {spesialmoduler.sha_plan.organisering}
                      </pre>
                    </div>
                  </Section>

                  <Section
                    title="SHA-plan - Risikovurdering"
                    icon="⚠️"
                    count="Byggeplass"
                    expanded={expandedSections['sha_risk']}
                    onToggle={() => toggleSection('sha_risk')}
                  >
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                        {spesialmoduler.sha_plan.risikovurdering}
                      </pre>
                    </div>
                  </Section>

                  <Section
                    title="SHA-plan - HMS-plan byggeprosjekt"
                    icon="📋"
                    count="Entreprenører"
                    expanded={expandedSections['sha_hms']}
                    onToggle={() => toggleSection('sha_hms')}
                  >
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                        {spesialmoduler.sha_plan.hms_plan}
                      </pre>
                    </div>
                  </Section>

                  <Section
                    title="SHA-plan - Spesielle risikoer"
                    icon="🚧"
                    count="Bygg-spesifikt"
                    expanded={expandedSections['sha_special']}
                    onToggle={() => toggleSection('sha_special')}
                  >
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                        {spesialmoduler.sha_plan.spesielle_risikoer}
                      </pre>
                    </div>
                  </Section>
                </>
              )}

              {/* HACCP (Servering/Uteliv) */}
              {spesialmoduler.haccp && (
                <>
                  <Section
                    title="HACCP - Farlig analyse"
                    icon="🍽️"
                    count="Mattrygghet"
                    expanded={expandedSections['haccp_hazard']}
                    onToggle={() => toggleSection('haccp_hazard')}
                  >
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                        {spesialmoduler.haccp.farlig_analyse}
                      </pre>
                    </div>
                  </Section>

                  <Section
                    title="HACCP - Kritiske kontrollpunkter (CCP)"
                    icon="🎯"
                    count="CCP"
                    expanded={expandedSections['haccp_ccp']}
                    onToggle={() => toggleSection('haccp_ccp')}
                  >
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                        {spesialmoduler.haccp.kritiske_kontrollpunkter}
                      </pre>
                    </div>
                  </Section>

                  <Section
                    title="HACCP - Overvåkning og tiltak"
                    icon="📊"
                    count="Kontroll"
                    expanded={expandedSections['haccp_monitor']}
                    onToggle={() => toggleSection('haccp_monitor')}
                  >
                    <div className="prose max-w-none">
                      <h4 className="font-semibold mb-2">Overvåkning:</h4>
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md mb-4">
                        {spesialmoduler.haccp.overvakning}
                      </pre>
                      <h4 className="font-semibold mb-2">Korrigerende tiltak:</h4>
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                        {spesialmoduler.haccp.korrigerende_tiltak}
                      </pre>
                    </div>
                  </Section>
                </>
              )}

              {/* Legionellakontroll (Eiendom) */}
              {spesialmoduler.legionella && (
                <>
                  <Section
                    title="Legionellakontroll - Risikofaktorer"
                    icon="💧"
                    count="Varmtvannssystemer"
                    expanded={expandedSections['leg_risk']}
                    onToggle={() => toggleSection('leg_risk')}
                  >
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                        {spesialmoduler.legionella.risikofaktorer}
                      </pre>
                    </div>
                  </Section>

                  <Section
                    title="Legionellakontroll - Kontrollrutiner"
                    icon="✅"
                    count="Månedlig/Halvårlig"
                    expanded={expandedSections['leg_control']}
                    onToggle={() => toggleSection('leg_control')}
                  >
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                        {spesialmoduler.legionella.kontrollrutiner}
                      </pre>
                    </div>
                  </Section>

                  <Section
                    title="Legionellakontroll - Temperaturkrav"
                    icon="🌡️"
                    count="60°C / 55°C / 20°C"
                    expanded={expandedSections['leg_temp']}
                    onToggle={() => toggleSection('leg_temp')}
                  >
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                        {spesialmoduler.legionella.temperaturkrav}
                      </pre>
                    </div>
                  </Section>

                  <Section
                    title="Legionellakontroll - Beredskap"
                    icon="🚨"
                    count="Ved mistanke"
                    expanded={expandedSections['leg_emergency']}
                    onToggle={() => toggleSection('leg_emergency')}
                  >
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                        {spesialmoduler.legionella.beredskap}
                      </pre>
                    </div>
                  </Section>
                </>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

// Section component for expandable content
function Section({ title, icon, count, expanded, onToggle, children }) {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center">
          <span className="text-2xl mr-3">{icon}</span>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{count}</p>
          </div>
        </div>
        <svg
          className={`w-6 h-6 text-gray-400 transition-transform ${expanded ? 'transform rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          {children}
        </div>
      )}
    </div>
  );
}

export default IKPackageViewer;
